import { inject, Injectable, Pipe, PipeTransform } from '@angular/core';
import {
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CommonUtils,
  Time,
} from '@infodata-group/inf-commons';
import { DayNotWorkService } from '../day-not-work.service';
import { AgendaDetailedOptions } from './agenda-detailed-options';
import { AgendaDetailedRow } from './agenda-detailed-row';
import { AgendaDetailedSubCell } from './agenda-detailed-sub-cell';
import { Telework } from '../telework';

@Pipe({
  name: 'timeFormat',
  standalone: true,
})
export class TimeFormatPipe implements PipeTransform {
  transform(value: Time): string {
    if (!value) {
      return '';
    }

    const hours = value.hours ?? 0;
    const minutes = value.minutes ?? 0;

    return `${this.pad(hours)}:${this.pad(minutes)}`;
  }

  private pad(num: number): string {
    return num.toString().padStart(2, '0');
  }
}

@Injectable({
  providedIn: 'root',
})
export class AgendaDetailedService {
  public dateStart: Date;
  public dateEnd: Date;
  public groups: CalendarTaskHeaderGroup[] = [];
  public rows: AgendaDetailedRow[] = [];
  public tasks: CalendarTask[];

  public draggingTask: CalendarTask;
  public outsideDraggingTask: CalendarDropTask<any>;

  private dayNotWorkService = inject(DayNotWorkService);

  public get rowHeight() {
    if (this.rows.length > 0) {
      return this.rows.at(0).height;
    }
    return 0;
  }

  public generateRows(
    date: Date,
    options: AgendaDetailedOptions,
    teleworks: Telework[],
  ) {
    this.rows = [];
    this.dateStart = new Date(date.getFullYear(), date.getMonth(), 1);
    this.dateEnd = new Date(date.getFullYear(), date.getMonth() + 2, 0);
    const current = new Date(this.dateStart);
    while (current <= this.dateEnd) {
      const resources = this.groups.flatMap((group) => group.tasksGroups);
      this.rows.push(
        new AgendaDetailedRow(new Date(current), resources, options),
      );
      current.setDate(current.getDate() + 1);
    }
    this.checkTeleworks(teleworks);
    this.checkDaysNotWorked();
  }

  private checkTeleworks(teleworks: Telework[]) {
    if (!teleworks) {
      return;
    }
    for (const row of this.rows) {
      const resourcesConcerned = teleworks.filter((telework) => {
        return telework.date === CommonUtils.convertDateToString(row.date);
      });
      if (resourcesConcerned.length > 0) {
        for (const resource of resourcesConcerned) {
          const resourceCellDay = row.resourcesCellsDay.find(
            (cell) => cell.resource.id === resource.resourceId,
          );
          if (resourceCellDay) {
            resourceCellDay.subCells.forEach((subCell) => {
              subCell.isTeleworked = true;
            });
          }
        }
      }
    }
  }

  private checkDaysNotWorked() {
    for (const row of this.rows) {
      if (!this.dayNotWorkService.isDayWork(row.date)) {
        row.resourcesCellsDay.forEach((cell) => {
          cell.subCells.forEach((subCell) => {
            subCell.isOff = true;
          });
        });
      }
    }
  }

  setTasks(calendarTasks: CalendarTask[]) {
    for (const row of this.rows) {
      const dayTasks = calendarTasks.filter((task) => task.inDay(row.date));
      row.setTasks(dayTasks);
    }
  }

  dropTask(cell: AgendaDetailedSubCell) {
    if (this.draggingTask) {
      const duration = this.draggingTask.getDuration();
      this.draggingTask.dateStart = cell.date
        .clone()
        .hour(cell.timeStart.hours)
        .minute(cell.timeStart.minutes);
      this.draggingTask.dateEnd = this.draggingTask.dateStart
        .clone()
        .add(CommonUtils.convertStrToTimeInMillis(duration), 'milliseconds');
      this.draggingTask.group = {
        header: cell.resource['group'],
        group: cell.resource['id'],
      };
      return;
    }
    if (this.outsideDraggingTask) {
      this.draggingTask = this.outsideDraggingTask.build(
        cell.date
          .clone()
          .hour(cell.timeStart.hours)
          .minute(cell.timeStart.minutes)
          .toDate(),
        {
          header: cell.resource['group'],
          group: cell.resource['id'],
        },
      );
    }
  }
}
